import {
  formatDateTimeIntoIsoDate,
  formatMoney,
} from "@/composables/formatUtils";
import { sumBy } from "lodash-es";
import type {
  ArrPriceSource,
  RevenueStreamGroup,
  RevenueStreamInfo,
  StreamTotals,
} from "./financialDashboardTypes";

export const STREAM_GROUP_LABELS: Record<RevenueStreamGroup, string> = {
  go: "GO trading",
  platform_fee: "Platform fees",
  one_off: "One-offs",
  adjustment: "Corrections",
};

export const STREAM_GROUP_ORDER: RevenueStreamGroup[] = [
  "go",
  "platform_fee",
  "one_off",
  "adjustment",
];

/** Solid (settled) and translucent (pending) fills per stream group, shared by every chart. */
export const STREAM_GROUP_COLORS: Record<
  RevenueStreamGroup,
  { solid: string; faded: string; border: string }
> = {
  go: {
    solid: "rgba(13, 110, 253, 0.85)",
    faded: "rgba(13, 110, 253, 0.3)",
    border: "#0d6efd",
  },
  platform_fee: {
    solid: "rgba(25, 135, 84, 0.85)",
    faded: "rgba(25, 135, 84, 0.3)",
    border: "#198754",
  },
  one_off: {
    solid: "rgba(253, 126, 20, 0.85)",
    faded: "rgba(253, 126, 20, 0.3)",
    border: "#fd7e14",
  },
  adjustment: {
    solid: "rgba(220, 53, 69, 0.85)",
    faded: "rgba(220, 53, 69, 0.3)",
    border: "#dc3545",
  },
};

/** Bootstrap text colour class for a revenue stream group. */
export const groupTextClass = (group: RevenueStreamGroup): string =>
  ({
    go: "text-primary",
    platform_fee: "text-success",
    one_off: "text-warning-emphasis",
    adjustment: "text-danger",
  })[group];

/** Percentage of `value` over `base` with one decimal; "0" when there is no base. */
export const pct = (value: number, base: number): string => {
  if (base === 0) return "0";
  return ((value / base) * 100).toFixed(1);
};

/** `YYYY-MM` label used consistently for month axes and tables. */
export const periodLabel = (year: number, month: number): string =>
  `${year}-${String(month).padStart(2, "0")}`;

/** Ratio (e.g. 1.12) rendered as a percentage string; dash when the ratio is undefined. */
export const formatRatio = (ratio: number | null | undefined): string => {
  if (ratio === null || ratio === undefined) return "–";
  return `${(ratio * 100).toFixed(1)}%`;
};

/** Signed percentage change from `previous` to `current`; null when there is no previous value. */
export const changeRatio = (
  current: number | null,
  previous: number | null | undefined
): number | null => {
  if (
    current === null ||
    previous === undefined ||
    previous === null ||
    previous === 0
  )
    return null;
  return (current - previous) / Math.abs(previous);
};

export const streamsInGroup = (
  streams: RevenueStreamInfo[],
  group: RevenueStreamGroup
): RevenueStreamInfo[] => streams.filter((stream) => stream.group === group);

/** Sum of a stream-total map over the streams of one group. */
export const groupTotal = (
  totals: StreamTotals | undefined,
  streams: RevenueStreamInfo[],
  group: RevenueStreamGroup
): number => {
  if (!totals) return 0;
  return sumBy(
    streamsInGroup(streams, group),
    (stream) => totals[stream.key] ?? 0
  );
};

/** Net revenue implied by a stream map: every stream adds except corrections, which subtract. */
export const netFromStreams = (totals: StreamTotals | undefined): number => {
  if (!totals) return 0;
  return sumBy(Object.entries(totals), ([key, amount]) =>
    key === "corrections" ? -amount : amount
  );
};

/** Same calendar day `months` ago, clamped to the target month's last day (31 May − 3 months is 29 Feb, not 3 Mar). */
export const isoMonthsAgo = (
  months: number,
  from: Date = new Date()
): string => {
  const target = new Date(from.getFullYear(), from.getMonth() - months, 1);
  const lastDay = new Date(
    target.getFullYear(),
    target.getMonth() + 1,
    0
  ).getDate();
  target.setDate(Math.min(from.getDate(), lastDay));
  return formatDateTimeIntoIsoDate(target);
};

/** ISO date (YYYY-MM-DD) `years` before today, for the default filter range. */
export const isoDateYearsAgo = (years: number): string =>
  isoMonthsAgo(years * 12);

export const isoToday = (): string => formatDateTimeIntoIsoDate(new Date());

/** A volume rounded to whole units, with thousands separators. */
export const formatWholeNumber = (value: number): string =>
  formatMoney(value, 0);

/** Money that may be unknown (no price for the month): a dash, never a zero. */
export const formatEurOrDash = (value: number | null): string =>
  value === null ? "–" : `${formatMoney(value)} €`;

/** Chart.js tooltip line for a money series: "<series label>: <amount> €". */
export const moneyTooltip = (
  label: string | undefined,
  value: number
): string => `${label}: ${formatMoney(value)} €`;

/** Human labels for where a month's GO price assumption came from. */
export const ARR_PRICE_SOURCE_LABELS: Record<ArrPriceSource, string> = {
  override: "your assumption",
  achieved_3m: "achieved, trailing 3 months",
  market_benchmark: "market benchmark bid",
  none: "no price available",
};

export type ArrStrategyKey =
  | "gwh_spot"
  | "gwh_spot_min_price"
  | "gwh_forward_committed"
  | "gwh_forward_to_be_sold"
  | "gwh_ppa"
  | "gwh_self_managed";

/** The sheet's strategy rows, in stacking order, with whether the volume earns commission. */
export const ARR_STRATEGIES: {
  key: ArrStrategyKey;
  label: string;
  color: string;
  sold: boolean;
}[] = [
  {
    key: "gwh_spot",
    label: "Spot sale",
    color: "rgba(13, 110, 253, 0.85)",
    sold: true,
  },
  {
    key: "gwh_spot_min_price",
    label: "Spot with min price",
    color: "rgba(13, 202, 240, 0.85)",
    sold: true,
  },
  {
    key: "gwh_forward_committed",
    label: "Forward (contracted)",
    color: "rgba(25, 135, 84, 0.85)",
    sold: true,
  },
  {
    key: "gwh_forward_to_be_sold",
    label: "Forward (planned, not yet contracted)",
    color: "rgba(25, 135, 84, 0.45)",
    sold: true,
  },
  {
    key: "gwh_ppa",
    label: "PPA",
    color: "rgba(255, 193, 7, 0.7)",
    sold: false,
  },
  {
    key: "gwh_self_managed",
    label: "Self managed",
    color: "rgba(173, 181, 189, 0.7)",
    sold: false,
  },
];
